/**
 * Client-side logging utility
 * Sends logs to the server for centralized logging
 */

// Default log levels
const LOG_LEVELS = {
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info',
  DEBUG: 'debug'
};

// Configuration settings
let config = {
  // Whether to also log to console (development mode)
  consoleOutput: process.env.NODE_ENV !== 'production',
  // Whether to send logs to server
  serverLogging: true,
  // Default level
  minLevel: process.env.NODE_ENV !== 'production' ? LOG_LEVELS.DEBUG : LOG_LEVELS.INFO,
  // API endpoint for logs
  logEndpoint: '/api/logs/client',
  // API endpoint for errors
  errorEndpoint: '/api/logs/error',
  // Maximum log size in bytes (100KB)
  maxLogSize: 100 * 1024
};

// Store original console methods
const originalConsole = {
  log: console.log,
  info: console.info,
  warn: console.warn,
  error: console.error,
  debug: console.debug
};

/**
 * Truncate large strings to prevent server errors
 * @param {string} str - The string to truncate
 * @param {number} maxLength - Maximum length allowed
 * @returns {string} - Truncated string
 */
const truncateString = (str, maxLength = 10000) => {
  if (typeof str !== 'string') return str;
  if (str.length <= maxLength) return str;
  
  return str.substring(0, maxLength) + `... [TRUNCATED - original length: ${str.length}]`;
};

/**
 * Safely converts an object to JSON string, handling circular references and size limits
 * @param {*} obj - The object to stringify
 * @param {number} maxSize - Maximum size in bytes
 * @returns {string} - A JSON string representation of the object
 */
const safeStringify = (obj, maxSize = config.maxLogSize) => {
  try {
    // Handle primitive types directly
    if (obj === null || obj === undefined || typeof obj !== 'object') {
      const result = JSON.stringify(obj);
      return truncateString(result, maxSize);
    }
    
    // Use a WeakSet to keep track of objects that have been seen
    const seen = new WeakSet();
    
    // Custom replacer function for JSON.stringify
    const replacer = (key, value) => {
      // Skip DOM nodes, React refs, and other non-serializable objects
      if (value instanceof Node || 
          (value && value.current) ||
          (value && value._reactInternals) ||
          (key === 'konvaLayerRef')) {
        return '[Object Reference]';
      }
      
      // Truncate very long strings (like base64 images)
      if (typeof value === 'string' && value.length > 1000) {
        // Check if it's a base64 image
        if (value.startsWith('data:image/')) {
          return `[Base64 Image - ${value.substring(0, 50)}... (${value.length} chars)]`;
        }
        return truncateString(value, 1000);
      }
      
      // For normal objects, handle circular references
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular Reference]';
        }
        seen.add(value);
      }
      
      return value;
    };
    
    const result = JSON.stringify(obj, replacer);
    
    // Check final size and truncate if necessary
    if (result.length > maxSize) {
      return result.substring(0, maxSize) + `... [TRUNCATED - original length: ${result.length}]`;
    }
    
    return result;
  } catch (error) {
    return `[Error during serialization: ${error.message}]`;
  }
};

/**
 * Configure the logger
 * @param {Object} options - Configuration options
 */
const configure = (options = {}) => {
  config = { ...config, ...options };
  
  // Set up console overrides if needed
  if (options.overrideConsole) {
    overrideConsoleMethods();
  }
};

/**
 * Send log to the server synchronously - no async/await to avoid issues in console override
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {Object} meta - Additional metadata
 */
const sendToServerSync = (level, message, meta = {}) => {
  if (!config.serverLogging) return;
  
  try {
    // Skip sending very large logs to prevent server errors
    const messageStr = typeof message === 'object' ? safeStringify(message) : String(message || '');
    const metaStr = safeStringify(meta);
    
    // If the total size is too large, skip server logging
    if (messageStr.length + metaStr.length > config.maxLogSize) {
      console.warn('[Logger] Skipping server log - too large:', messageStr.length + metaStr.length, 'bytes');
      return;
    }
    
    // Get auth token if available
    const token = localStorage.getItem('authToken');
    
    // Build request
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Add authorization if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Format message for logging - use safeStringify to handle circular references
    const formattedMessage = typeof message === 'object' 
      ? safeStringify(message) 
      : String(message || '');
    
    // Build log data and safely stringify the meta object
    const logData = {
      level,
      message: formattedMessage,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...(typeof meta === 'object' ? JSON.parse(safeStringify(meta)) : { meta })
    };
    
    // Choose endpoint based on level
    const endpoint = level === LOG_LEVELS.ERROR 
      ? config.errorEndpoint 
      : config.logEndpoint;
    
    // Use synchronous XMLHttpRequest to avoid async issues in console override
    const xhr = new XMLHttpRequest();
    xhr.open('POST', endpoint, false); // false makes it synchronous
    
    // Set headers
    xhr.setRequestHeader('Content-Type', 'application/json');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    
    // Send the request
    xhr.send(safeStringify(logData));
    
  } catch (error) {
    // Only log to console if sending to server fails
    originalConsole.error('Error sending log to server:', error);
  }
};

/**
 * Send log to the server asynchronously
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {Object} meta - Additional metadata
 */
const sendToServer = async (level, message, meta = {}) => {
  if (!config.serverLogging) return;
  
  try {
    // Skip sending very large logs to prevent server errors
    const messageStr = typeof message === 'object' ? safeStringify(message) : String(message || '');
    const metaStr = safeStringify(meta);
    
    // If the total size is too large, skip server logging
    if (messageStr.length + metaStr.length > config.maxLogSize) {
      console.warn('[Logger] Skipping server log - too large:', messageStr.length + metaStr.length, 'bytes');
      return;
    }
    
    // Get auth token if available
    const token = localStorage.getItem('authToken');
    
    // Build request
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Add authorization if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Format message for logging - use safeStringify to handle circular references
    const formattedMessage = typeof message === 'object' 
      ? safeStringify(message) 
      : String(message || '');
    
    // Build log data and safely stringify the meta object
    const logData = {
      level,
      message: formattedMessage,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...(typeof meta === 'object' ? JSON.parse(safeStringify(meta)) : { meta })
    };
    
    // Choose endpoint based on level
    const endpoint = level === LOG_LEVELS.ERROR 
      ? config.errorEndpoint 
      : config.logEndpoint;
    
    // Send log to server
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: safeStringify(logData)
    });
    
    // Check if request was successful
    if (!response.ok) {
      originalConsole.error('Failed to send log to server:', response.statusText);
    }
  } catch (error) {
    // Only log to console if sending to server fails
    originalConsole.error('Error sending log to server:', error);
  }
};

/**
 * Override console methods to also send logs to server
 */
const overrideConsoleMethods = () => {
  // Override console.log
  console.log = (...args) => {
    const message = args.length > 0 ? args[0] : '';
    const meta = args.length > 1 ? { additionalArgs: args.slice(1) } : {};
    
    // Call original method
    originalConsole.log(...args);
    
    // Send to server synchronously to avoid async issues
    sendToServerSync(LOG_LEVELS.INFO, message, meta);
  };
  
  // Override console.info
  console.info = (...args) => {
    const message = args.length > 0 ? args[0] : '';
    const meta = args.length > 1 ? { additionalArgs: args.slice(1) } : {};
    
    // Call original method
    originalConsole.info(...args);
    
    // Send to server synchronously
    sendToServerSync(LOG_LEVELS.INFO, message, meta);
  };
  
  // Override console.warn
  console.warn = (...args) => {
    const message = args.length > 0 ? args[0] : '';
    const meta = args.length > 1 ? { additionalArgs: args.slice(1) } : {};
    
    // Call original method
    originalConsole.warn(...args);
    
    // Send to server synchronously
    sendToServerSync(LOG_LEVELS.WARN, message, meta);
  };
  
  // Override console.error
  console.error = (...args) => {
    const message = args.length > 0 ? args[0] : '';
    const meta = args.length > 1 ? { additionalArgs: args.slice(1) } : {};
    
    // Call original method
    originalConsole.error(...args);
    
    // Send to server synchronously
    sendToServerSync(LOG_LEVELS.ERROR, message, meta);
  };
  
  // Override console.debug
  console.debug = (...args) => {
    const message = args.length > 0 ? args[0] : '';
    const meta = args.length > 1 ? { additionalArgs: args.slice(1) } : {};
    
    // Call original method
    originalConsole.debug(...args);
    
    // Send to server synchronously
    sendToServerSync(LOG_LEVELS.DEBUG, message, meta);
  };
};

/**
 * Log to console if enabled
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {Object} meta - Additional metadata
 */
const logToConsole = (level, message, meta = {}) => {
  if (!config.consoleOutput) return;
  
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
  
  switch (level) {
    case LOG_LEVELS.ERROR:
      originalConsole.error(prefix, message, meta);
      break;
    case LOG_LEVELS.WARN:
      originalConsole.warn(prefix, message, meta);
      break;
    case LOG_LEVELS.DEBUG:
      originalConsole.debug(prefix, message, meta);
      break;
    case LOG_LEVELS.INFO:
    default:
      originalConsole.info(prefix, message, meta);
      break;
  }
};

/**
 * Check if the log level should be processed
 * @param {string} level - The log level to check
 * @returns {boolean} - Whether the level should be logged
 */
const shouldLog = (level) => {
  const levels = [LOG_LEVELS.ERROR, LOG_LEVELS.WARN, LOG_LEVELS.INFO, LOG_LEVELS.DEBUG];
  const configLevelIndex = levels.indexOf(config.minLevel);
  const levelIndex = levels.indexOf(level);
  
  return levelIndex <= configLevelIndex;
};

/**
 * Log a message
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {Object} meta - Additional metadata
 */
const log = (level, message, meta = {}) => {
  if (!shouldLog(level)) return;
  
  logToConsole(level, message, meta);
  sendToServer(level, message, meta);
};

// Public API
const logger = {
  // Main logging methods
  error: (message, meta = {}) => log(LOG_LEVELS.ERROR, message, meta),
  warn: (message, meta = {}) => log(LOG_LEVELS.WARN, message, meta),
  info: (message, meta = {}) => log(LOG_LEVELS.INFO, message, meta),
  debug: (message, meta = {}) => log(LOG_LEVELS.DEBUG, message, meta),
  
  // Configuration
  configure,
  
  // Override console methods
  overrideConsole: overrideConsoleMethods,
  
  // Create a error boundary component for React
  createErrorLogger: (error, info) => {
    const { componentStack } = info || {};
    
    logger.error('Unhandled React Error', {
      message: error?.message,
      stack: error?.stack,
      componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href
    });
  }
};

export default logger; 